import { useEffect, useState, useCallback } from "react";
import api, { formatApiError, money } from "@/lib/api";
import { PageHeader, StatCard } from "@/components/common";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { PhoneCall, Broadcast, CurrencyDollar, Plus } from "@phosphor-icons/react";

const toDollars = (m) => (m == null ? null : (m / 100).toFixed(2));

export default function CallLimit() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [channels, setChannels] = useState("");
  const [quote, setQuote] = useState(null);
  const [quoting, setQuoting] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [buying, setBuying] = useState(false);

  const load = useCallback(async () => {
    try {
      const res = await api.get("/billing/call-limit");
      setData(res.data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const limit = data?.concurrent_call_limit;
  const noCapacity = limit == null || limit <= 0;
  const price = toDollars(data?.call_limit_price_minor);
  const noPrice = data && data.call_limit_price_minor == null;
  const canPurchase = data && !noPrice;

  // Fetch a fresh quote whenever the channel count changes.
  useEffect(() => {
    const n = parseInt(channels, 10);
    if (!canPurchase || !n || n <= 0) { setQuote(null); return; }
    let cancelled = false;
    setQuoting(true);
    const timer = setTimeout(async () => {
      try {
        const { data: q } = await api.get("/billing/call-limit/quote", { params: { channels: n } });
        if (!cancelled) setQuote(q);
      } catch (e) {
        if (!cancelled) { setQuote(null); toast.error(formatApiError(e.response?.data?.detail)); }
      } finally {
        if (!cancelled) setQuoting(false);
      }
    }, 300);
    return () => { cancelled = true; clearTimeout(timer); };
  }, [channels, canPurchase]);

  const purchase = async () => {
    const n = parseInt(channels, 10);
    setBuying(true);
    try {
      const { data: res } = await api.post("/billing/call-limit/purchase", { channels: n });
      toast.success(`Added ${res.channels_added} channel(s) — new limit ${res.concurrent_call_limit}. Charged ${money(res.charge_minor)}.`);
      setConfirmOpen(false); setChannels(""); setQuote(null);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    } finally {
      setBuying(false);
    }
  };

  return (
    <div className="space-y-6" data-testid="call-limit-page">
      <PageHeader title="Call Limit"
        subtitle="Your CONCURRENT call limit — the maximum number of calls you can run at the same time. When a call ends its slot frees up; the limit itself never decreases." />
      {loading ? (
        <p className="text-sm text-muted-foreground">Loading…</p>
      ) : (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-6">
            <StatCard testid="cl-limit" icon={PhoneCall} tone="primary"
              label="Concurrent Call Limit"
              value={noCapacity ? "No calls" : String(limit)}
              hint={noCapacity ? "No concurrent capacity — calls will fail" : "Max simultaneous active calls"} />
            <StatCard testid="cl-active" icon={Broadcast} tone="success"
              label="Active Now" value={String(data?.active_calls ?? 0)}
              hint="Calls in progress right now" />
            <StatCard testid="cl-price" icon={CurrencyDollar} tone="warning"
              label="Price / Channel"
              value={price == null ? "—" : `$${price}`}
              hint={data?.call_limit_pricing_mode ? `${data.call_limit_pricing_mode} pricing` : "per concurrent channel"} />
            <StatCard testid="cl-balance" icon={CurrencyDollar} tone="primary"
              label="Cash Balance" value={money(data?.cash_balance_minor || 0)}
              hint="Used to buy channels" />
          </div>

          <Card className="p-6 rounded-xl border-border max-w-xl" data-testid="purchase-card">
            <h3 className="font-semibold text-base">Add Concurrent Channels</h3>
            <p className="text-sm text-muted-foreground mt-0.5">
              Buy more simultaneous-call capacity. Charged pro-rata for the days left in this month at your per-channel price.
            </p>

            {noPrice && (
              <p className="mt-4 text-sm text-destructive" data-testid="purchase-no-price">
                No per-channel price configured — contact your provider.
              </p>
            )}

            {canPurchase && (
              <div className="mt-4 space-y-4">
                {noCapacity && (
                  <p className="text-sm text-muted-foreground" data-testid="purchase-bootstrap-hint">
                    You currently have no capacity — buy channels below to start calling.
                  </p>
                )}
                <div className="space-y-1.5 max-w-[220px]">
                  <Label>Channels to add</Label>
                  <Input data-testid="purchase-channels" type="number" min="1" step="1"
                    value={channels} onChange={(e) => setChannels(e.target.value)} placeholder="e.g. 5" />
                </div>

                {quote && quote.charge_minor != null && (
                  <div className="rounded-lg border border-border bg-muted/40 p-4 space-y-1.5 text-sm" data-testid="purchase-summary">
                    <Row label="Price per channel" value={`$${toDollars(quote.call_limit_price_minor)}`} />
                    <Row label={`Prorated (${quote.remaining_days}/${quote.days_in_month} days left)`}
                      value={money(quote.charge_minor)} testid="purchase-charge" strong />
                    <Row label="New limit" value={`${quote.concurrent_call_limit} → ${quote.new_limit}`} testid="purchase-new-limit" />
                    <div className="border-t border-border my-1" />
                    <Row label="Current balance" value={money(quote.cash_balance_minor)} />
                    <Row label="Balance after purchase"
                      value={money(quote.balance_after_minor)}
                      testid="purchase-balance-after"
                      strong
                      danger={!quote.sufficient} />
                    {!quote.sufficient && (
                      <p className="text-destructive text-xs pt-1" data-testid="purchase-insufficient">
                        Insufficient balance — add cash on the Payments page first.
                      </p>
                    )}
                  </div>
                )}

                <Button data-testid="purchase-btn" className="rounded-lg"
                  disabled={quoting || !quote || quote.charge_minor == null || !quote.sufficient}
                  onClick={() => setConfirmOpen(true)}>
                  <Plus className="w-4 h-4 mr-1.5" weight="bold" /> Purchase channels
                </Button>
              </div>
            )}
          </Card>

          <Card className="p-5 rounded-xl border-border max-w-xl">
            <p className="text-sm text-muted-foreground">
              <b>Example:</b> a limit of 10 means up to 10 calls can be active at once. An 11th call waits
              until a slot frees. This is not calls per day, per month, total calls, or credits.
            </p>
          </Card>
        </>
      )}

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent data-testid="purchase-confirm-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Confirm channel purchase</AlertDialogTitle>
            <AlertDialogDescription>
              {quote && `This will add ${quote.channels} concurrent channel(s) (new limit ${quote.new_limit}), charge ${money(quote.charge_minor)} now, and leave a balance of ${money(quote.balance_after_minor)}.`}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="purchase-cancel-btn">Cancel</AlertDialogCancel>
            <AlertDialogAction data-testid="purchase-confirm-btn" disabled={buying}
              onClick={(e) => { e.preventDefault(); purchase(); }}>
              {buying ? "Processing…" : "Confirm purchase"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}

function Row({ label, value, testid, strong, danger }) {
  return (
    <div className="flex items-center justify-between">
      <span className="text-muted-foreground">{label}</span>
      <span data-testid={testid}
        className={`tabular-nums ${strong ? "font-semibold" : ""} ${danger ? "text-destructive" : ""}`}>
        {value}
      </span>
    </div>
  );
}
